import { useConfirm } from "../context/ConfirmContext";
import { AsyncButton } from "../components/AsyncButton";
import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOffers, getOffer, generateOffer, sendOffer, updateOffer, deleteOffer, downloadOfferPdf,
  getDocTemplates,
  getCandidates, getJobs, getLocale, getCurrencies, quickCreateCandidate,
  getOfferApprovers, submitOfferForApproval, approveOffer, rejectOffer, getOfferSignatureEvents,
  downloadOfferCertificate,
} from "../api";
import { useToast } from "../context/ToastContext";
import { SkeletonRow } from "../components/Skeleton";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAuth } from "../context/AuthContext";

const NAVY = "#1e3a8a";
const TITLE_COLOR = "#0f172a";
const TEAL = "#00c9a7";

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft:    { bg: "#f8fafc", text: "#475569", border: "#e2e8f0" },
  sent:     { bg: "#eff6ff", text: "#1d4ed8", border: "#93c5fd" },
  accepted: { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  declined: { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
};

const APPROVAL_COLORS: Record<string, { bg: string; text: string; border: string; label: string }> = {
  pending:  { bg: "#fffbeb", text: "#b45309", border: "#fde68a", label: "⏳ Awaiting sign-off" },
  approved: { bg: "#f0fdf4", text: "#166534", border: "#86efac", label: "✓ Signed off" },
  rejected: { bg: "#fef2f2", text: "#dc2626", border: "#fecaca", label: "✗ Sign-off rejected" },
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 };
const btnPrimary: React.CSSProperties = {
  background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white",
  border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13,
  fontWeight: 700, cursor: "pointer",
};
const btnOutline: React.CSSProperties = {
  background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
  padding: "9px 16px", fontSize: 13, color: "#64748b", cursor: "pointer",
};

export default function OffersPage({ initialCandidateId, initialOfferId }: { initialCandidateId?: number; initialOfferId?: number }) {
  const qc = useQueryClient();
  const tz = useTenantTimezone();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showGenerate, setShowGenerate] = useState(!!initialCandidateId);
  const [editOffer, setEditOffer] = useState<any>(null);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState<number | null>(null);
  const [showSavePortal, setShowSavePortal] = useState(false);
  const [portalForm, setPortalForm] = useState({ name: "", email: "", current_role: "", phone: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [approveTypedName, setApproveTypedName] = useState("");
  const [approveConsentChecked, setApproveConsentChecked] = useState(false);
  const [showSignatureEvents, setShowSignatureEvents] = useState(false);

  const [sending, setSending] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Generate form state
  const blankGenForm = () => ({
    candidate_id: "",
    candidate_name: "",
    candidate_email: "",
    job_id: "",
    salary: "",
    template_id: "",
    use_template: true,
    currency: "USD",
    currency_symbol: "$",
    start_date: "",
    expiry_date: "",
    department: "",
  });
  const [genForm, setGenForm] = useState({
    ...blankGenForm(),
    candidate_id: initialCandidateId ? String(initialCandidateId) : "",
  });

  const { data: offers = [], isLoading } = useQuery({ queryKey: ["offers"], queryFn: getOffers });
  const isMobile = useIsMobile(768);
  const [search, setSearch] = useState("");

  // Offers has no tabs, no pagination and — until now — no search. The artifact
  // flags that directly: "the page can grow past a screenful fast." A tenant with
  // 59 candidates accumulates dozens of offers and had no way to find one.
  const visibleOffers = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return offers as any[];
    return (offers as any[]).filter((o: any) =>
      o.candidate_name?.toLowerCase().includes(q) ||
      o.job_title?.toLowerCase().includes(q) ||
      o.department?.toLowerCase().includes(q)
    );
  }, [offers, search]);

  // Deep link from an approval-requested email/notification (?offer_id=) — open
  // straight into the edit modal instead of just landing on the bare list.
  React.useEffect(() => {
    if (initialOfferId) {
      getOffer(initialOfferId).then(setEditOffer).catch(() => toast("Offer not found", "error"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOfferId]);

  const { data: candidatesData } = useQuery({ queryKey: ["candidates"], queryFn: () => getCandidates() });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => getJobs() });
  const { data: locale } = useQuery({ queryKey: ["locale"], queryFn: getLocale });
  const { data: currencies = [] } = useQuery({ queryKey: ["currencies"], queryFn: getCurrencies });
  const { data: approvers = [] } = useQuery({ queryKey: ["offer-approvers"], queryFn: getOfferApprovers });
  const { data: signatureEvents = [] } = useQuery({
    queryKey: ["offer-signature-events", editOffer?.id],
    queryFn: () => getOfferSignatureEvents(editOffer.id),
    enabled: showSignatureEvents && !!editOffer?.id,
  });

  React.useEffect(() => {
    if (locale) {
      setGenForm(f => ({
        ...f,
        currency: locale.currency || f.currency,
        currency_symbol: locale.currency_symbol || f.currency_symbol,
      }));
    }
  }, [locale]);

  // Defaults a freshly-generated offer's approver to the creator themselves —
  // still changeable to anyone else in the picker before submitting for approval.
  React.useEffect(() => {
    if (editOffer && !editOffer.approver_id && !editOffer.approval_status && user?.id && !assignApproverMutation.isPending) {
      assignApproverMutation.mutate({ id: editOffer.id, approver_id: user.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOffer?.id]);

  function onOfferCurrencyChange(code: string) {
    const found = (currencies as any[]).find((c: any) => c.code === code);
    setGenForm(f => ({ ...f, currency: code, currency_symbol: found?.symbol || f.currency_symbol }));
  }

  const candidates = (candidatesData as any)?.candidates || [];

  // The tenant's offer templates. Empty for anyone who has never written one,
  // which hides the select entirely so the form is unchanged for them (AC26).
  const { data: offerTemplates = [] } = useQuery({
    queryKey: ["doc-templates", "offer"],
    queryFn: () => getDocTemplates({ doc_family: "offer" }),
  });
  const activeOfferTemplates = (offerTemplates as any[]).filter((tpl: any) => tpl.is_active);
  const defaultOfferTemplate = activeOfferTemplates.find((tpl: any) => tpl.is_default);

  const generateMutation = useMutation({
    mutationFn: generateOffer,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setShowGenerate(false);
      setEditOffer(data);
      toast("Offer letter generated");
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast(msg || "Failed to generate offer", "error");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateOffer(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setEditOffer(null);
      toast("Offer letter saved");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to save offer", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      toast("Offer letter deleted", "info");
    },
  });

  const assignApproverMutation = useMutation({
    mutationFn: ({ id, approver_id }: { id: number; approver_id: number }) => updateOffer(id, { approver_id }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setEditOffer(data);
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to assign approver", "error"),
  });

  const submitApprovalMutation = useMutation({
    mutationFn: submitOfferForApproval,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setEditOffer(data);
      toast("Submitted for approval");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to submit for approval", "error"),
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, typedName, consentChecked }: { id: number; typedName: string; consentChecked: boolean }) =>
      approveOffer(id, typedName, consentChecked),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setEditOffer(data);
      setApproveTypedName("");
      setApproveConsentChecked(false);
      toast("Offer approved ✓", "success");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to approve", "error"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => rejectOffer(id, reason),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      setEditOffer(data);
      setShowReject(false);
      setRejectReason("");
      toast("Offer sign-off rejected", "info");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to reject", "error"),
  });

  const quickCreateMutation = useMutation({
    mutationFn: quickCreateCandidate,
    onSuccess: (created: any) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      setGenForm(f => ({ ...f, candidate_id: String(created.candidate_id), candidate_name: portalForm.name }));
      setShowSavePortal(false);
      setPortalForm({ name: "", email: "", current_role: "", phone: "" });
      toast("Candidate saved to portal");
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast(msg || "Failed to save candidate", "error");
    },
  });

  async function handleSend(id: number) {
    const source = editOffer?.id === id ? editOffer : offers.find((o: any) => o.id === id);
    const label = source?.candidate_email
      ? `${source.candidate_name} (${source.candidate_email})`
      : source?.candidate_name || "the candidate";
    const notApproved = source?.approval_status !== "approved";
    const message = notApproved
      ? `This offer hasn't been signed off by an approver yet. Send it to ${label} anyway?`
      : `Send this offer letter to ${label}?`;
    const ok = await confirm(message, {
      title: notApproved ? "Send without sign-off?" : "Send offer",
      confirmLabel: "Send",
      danger: notApproved,
    });
    if (!ok) return;
    setSending(id);
    try {
      const updated = await sendOffer(id);
      qc.invalidateQueries({ queryKey: ["offers"] });
      if (editOffer?.id === id) setEditOffer(updated);
      toast("Offer sent to candidate ✓", "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to send — add a candidate email first", "error");
    } finally { setSending(null); }
  }

  async function handleDownload(id: number) {
    setDownloading(id);
    try {
      const { blob, filename } = await downloadOfferPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to download PDF", "error"); }
    finally { setDownloading(null); }
  }

  async function handleDownloadCertificate(id: number) {
    setDownloadingCertificate(id);
    try {
      const { blob, filename } = await downloadOfferCertificate(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to download certificate", "error");
    }
    finally { setDownloadingCertificate(null); }
  }

  function handleGenSubmit(e: React.FormEvent) {
    e.preventDefault();
    generateMutation.mutate({
      candidate_id: genForm.candidate_id ? Number(genForm.candidate_id) : undefined,
      candidate_name: genForm.candidate_name || undefined,
      candidate_email: genForm.candidate_email || undefined,
      job_id: genForm.job_id ? Number(genForm.job_id) : undefined,
      salary: Number(genForm.salary) || 0,
      currency: genForm.currency,
      currency_symbol: genForm.currency_symbol,
      start_date: genForm.start_date || undefined,
      expiry_date: genForm.expiry_date || undefined,
      department: genForm.department || undefined,
      template_id: genForm.template_id ? Number(genForm.template_id) : undefined,
      use_template: genForm.use_template !== false,
    });
  }

  return (
    <div>
      <LoadingOverlay active={
        generateMutation.isPending || updateMutation.isPending || deleteMutation.isPending ||
        quickCreateMutation.isPending || sending !== null || downloading !== null ||
        submitApprovalMutation.isPending || approveMutation.isPending || rejectMutation.isPending ||
        downloadingCertificate !== null
      } />
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ margin: 0, color: TITLE_COLOR, fontSize: 24, fontWeight: 700 }}>Offer Letters</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>AI-generated, editable offer letters with PDF download</p>
        </div>
        <button onClick={() => { setGenForm(blankGenForm()); setShowGenerate(true); }} style={btnPrimary}>+ Generate Offer</button>
      </div>

      {/* Search — the page has no tabs or pagination, so this is the only way
          to find one offer among dozens. */}
      {(offers as any[]).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search candidate, job or department…"
            style={{
              padding: isMobile ? "11px 14px" : "8px 14px", minHeight: isMobile ? 44 : undefined,
              borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13,
              width: isMobile ? "100%" : 320, outline: "none", color: "#1e293b", boxSizing: "border-box",
            }} />
        </div>
      )}

      {/* Generate modal */}
      {showGenerate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowGenerate(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 14, padding: 28, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <h3 style={{ margin: "0 0 20px", color: NAVY, fontSize: 17 }}>Generate Offer Letter</h3>
            <form onSubmit={handleGenSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Candidate Name *</label>
                  <button type="button" onClick={() => { setShowSavePortal(v => !v); setPortalForm(p => ({ ...p, name: genForm.candidate_name })); }}
                    style={{ fontSize: 11, color: TEAL, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                    {showSavePortal ? "✕ Cancel" : "＋ Save to portal"}
                  </button>
                </div>
                <input
                  required
                  value={genForm.candidate_name}
                  onChange={e => setGenForm(f => ({ ...f, candidate_name: e.target.value, candidate_id: "" }))}
                  placeholder="e.g. Jane Smith — type any name to generate"
                  style={inputStyle}
                />
                {candidates.length > 0 && (
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                    Pick existing:{" "}
                    <select
                      value={genForm.candidate_id}
                      onChange={e => {
                        const cid = e.target.value;
                        const found = candidates.find((c: any) => String(c.candidate_id) === cid);
                        setGenForm(f => ({ ...f, candidate_id: cid, candidate_name: found ? found.name : f.candidate_name }));
                      }}
                      style={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 6, padding: "2px 6px", color: "#475569" }}
                    >
                      <option value="">— select —</option>
                      {candidates.map((c: any) => <option key={c.candidate_id} value={c.candidate_id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                {showSavePortal && (
                  <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 8, padding: 12, marginTop: 8, display: "flex", flexDirection: "column", gap: 10, background: "#f8fafc" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Full Name *</label>
                        <input required value={portalForm.name}
                          onChange={e => { setPortalForm(f => ({ ...f, name: e.target.value })); setGenForm(f => ({ ...f, candidate_name: e.target.value })); }}
                          placeholder="Jane Smith" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Email *</label>
                        <input required type="email" value={portalForm.email}
                          onChange={e => setPortalForm(f => ({ ...f, email: e.target.value }))}
                          placeholder="jane@example.com" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Current Role</label>
                        <input value={portalForm.current_role}
                          onChange={e => setPortalForm(f => ({ ...f, current_role: e.target.value }))}
                          placeholder="Software Engineer" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Phone</label>
                        <input value={portalForm.phone}
                          onChange={e => setPortalForm(f => ({ ...f, phone: e.target.value }))}
                          placeholder="+91 9999999999" style={inputStyle} />
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button type="button"
                        disabled={quickCreateMutation.isPending || !portalForm.name || !portalForm.email}
                        onClick={() => quickCreateMutation.mutate(portalForm)}
                        style={{ ...btnPrimary, padding: "8px 16px", fontSize: 12 }}>
                        {quickCreateMutation.isPending ? "Saving…" : "Save Candidate"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* Email — only needed for freeform candidates not in portal */}
              {!genForm.candidate_id && (
                <div>
                  <label style={labelStyle}>Candidate Email <span style={{ color: "#94a3b8", fontWeight: 400 }}>(to send offer)</span></label>
                  <input type="email" value={genForm.candidate_email}
                    onChange={e => setGenForm(f => ({ ...f, candidate_email: e.target.value }))}
                    placeholder="jane@example.com" style={inputStyle} />
                </div>
              )}

              <div>
                <label style={labelStyle}>Job / Position</label>
                <select value={genForm.job_id} onChange={e => setGenForm(f => ({ ...f, job_id: e.target.value }))} style={inputStyle}>
                  <option value="">Select job (optional)…</option>
                  {jobs.map((j: any) => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <input value={genForm.department} onChange={e => setGenForm(f => ({ ...f, department: e.target.value }))}
                  placeholder="e.g. Engineering" style={inputStyle} />
              </div>
              <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Monthly Salary *</label>
                  <div style={{ display: "flex", gap: 0 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", padding: "0 10px",
                      background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRight: "none",
                      borderRadius: "8px 0 0 8px", fontSize: 14, fontWeight: 700, color: "#475569",
                      minWidth: 36, justifyContent: "center",
                    }}>{genForm.currency_symbol}</span>
                    <input required type="number" min="0" value={genForm.salary}
                      onChange={e => setGenForm(f => ({ ...f, salary: e.target.value }))}
                      placeholder="0.00"
                      style={{ ...inputStyle, borderRadius: "0 8px 8px 0", flex: 1 }} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select value={genForm.currency} onChange={e => onOfferCurrencyChange(e.target.value)}
                    style={{ ...inputStyle, cursor: "pointer" }}>
                    {(currencies as any[]).length > 0
                      ? (currencies as any[]).map((c: any) => (
                          <option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>
                        ))
                      : <option value={genForm.currency}>{genForm.currency}</option>
                    }
                  </select>
                </div>
              </div>
              <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <DateInput value={genForm.start_date} onChange={e => setGenForm(f => ({ ...f, start_date: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }} />
                </div>
                <div>
                  <label style={labelStyle}>Offer Expires</label>
                  <DateInput value={genForm.expiry_date} onChange={e => setGenForm(f => ({ ...f, expiry_date: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }} />
                </div>
              </div>

              {activeOfferTemplates.length > 0 && (
                <div>
                  <label style={labelStyle}>Template</label>
                  <select
                    value={genForm.use_template === false ? "__freeform__"
                           : (genForm.template_id || "__default__")}
                    onChange={e => {
                      const v = e.target.value;
                      setGenForm(f => ({
                        ...f,
                        use_template: v !== "__freeform__",
                        template_id: (v === "__freeform__" || v === "__default__") ? "" : v,
                      }));
                    }}
                    style={inputStyle}>
                    <option value="__default__">
                      {defaultOfferTemplate
                        ? `${defaultOfferTemplate.name} (default)`
                        : "No template — AI writes it all"}
                    </option>
                    {activeOfferTemplates
                      .filter((tpl: any) => !defaultOfferTemplate || tpl.id !== defaultOfferTemplate.id)
                      .map((tpl: any) => (
                        <option key={tpl.id} value={String(tpl.id)}>{tpl.name}</option>
                      ))}
                    <option value="__freeform__">— AI freeform (ignore templates) —</option>
                  </select>
                  {defaultOfferTemplate && genForm.use_template !== false && !genForm.template_id && (
                    <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4 }}>
                      {defaultOfferTemplate.block_count - defaultOfferTemplate.ai_block_count} fixed
                      {" "}sections · {defaultOfferTemplate.ai_block_count} written by AI · v{defaultOfferTemplate.version}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button type="button" onClick={() => setShowGenerate(false)} style={btnOutline}>Cancel</button>
                <button type="submit" disabled={generateMutation.isPending} style={btnPrimary}>
                  {generateMutation.isPending ? "Generating…" : "✨ Generate with AI"}
                </button>
              </div>
              {generateMutation.isPending && (
                <p style={{ fontSize: 12, color: "#64748b", margin: 0, textAlign: "center" }}>
                  AI is drafting the offer letter… this may take a moment.
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Edit / Review modal */}
      {editOffer && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setEditOffer(null)}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(720px, calc(100vw - 32px))", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: NAVY, fontSize: 17 }}>
                Review &amp; Edit Offer Letter
                {editOffer.template && (
                  <span style={{ display: "block", fontSize: 11.5, fontWeight: 500, color: "#0369a1", marginTop: 3 }}>
                    From {editOffer.template.template_name} v{editOffer.template.version}
                  </span>
                )}
              </h3>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {editOffer.candidate_name} — {editOffer.job_title}
              </span>
            </div>

            <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Job Title</label>
                <input value={editOffer.job_title || ""} onChange={e => setEditOffer((o: any) => ({ ...o, job_title: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <input value={editOffer.department || ""} onChange={e => setEditOffer((o: any) => ({ ...o, department: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Candidate Email</label>
                <input type="email" value={editOffer.candidate_email || ""} onChange={e => setEditOffer((o: any) => ({ ...o, candidate_email: e.target.value }))} placeholder="candidate@email.com" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Monthly Salary</label>
                <input type="number" value={editOffer.salary || ""} onChange={e => setEditOffer((o: any) => ({ ...o, salary: Number(e.target.value) }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={editOffer.status} onChange={e => setEditOffer((o: any) => ({ ...o, status: e.target.value }))} style={inputStyle}>
                  {["draft", "sent", "accepted", "declined"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start Date</label>
                <DateInput value={editOffer.start_date || ""} onChange={e => setEditOffer((o: any) => ({ ...o, start_date: e.target.value }))} style={{ ...inputStyle, cursor: "pointer" }} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Offer Expires</label>
                <DateInput value={editOffer.expiry_date || ""} onChange={e => setEditOffer((o: any) => ({ ...o, expiry_date: e.target.value }))} style={{ ...inputStyle, maxWidth: "calc(50% - 6px)", cursor: "pointer" }} />
              </div>
            </div>

            {/* Approval sign-off */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Approver Sign-off</label>
                {editOffer.approval_status && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                    background: APPROVAL_COLORS[editOffer.approval_status]?.bg,
                    color: APPROVAL_COLORS[editOffer.approval_status]?.text,
                    border: `1px solid ${APPROVAL_COLORS[editOffer.approval_status]?.border}`,
                  }}>{APPROVAL_COLORS[editOffer.approval_status]?.label}</span>
                )}
              </div>

              {editOffer.approval_status !== "approved" && (
                <div style={{ marginBottom: 8 }}>
                  <select
                    value={editOffer.approver_id || ""}
                    disabled={editOffer.approval_status === "pending"}
                    onChange={e => e.target.value && assignApproverMutation.mutate({ id: editOffer.id, approver_id: Number(e.target.value) })}
                    style={{ ...inputStyle, cursor: editOffer.approval_status === "pending" ? "not-allowed" : "pointer" }}
                  >
                    <option value="">Select an approver…</option>
                    {(approvers as any[]).map((a: any) => (
                      <option key={a.id} value={a.id}>{a.name}{a.id === user?.id ? " (you)" : ""}</option>
                    ))}
                  </select>
                </div>
              )}

              {editOffer.approval_status === "rejected" && editOffer.rejection_reason && (
                <p style={{ fontSize: 12, color: "#dc2626", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "8px 10px", margin: "0 0 8px" }}>
                  Rejected: {editOffer.rejection_reason}
                </p>
              )}

              {editOffer.approval_status === "approved" && (
                <p style={{ fontSize: 12, color: "#166534", margin: 0 }}>
                  Signed off by {editOffer.approver_name} on {editOffer.approved_at ? formatDate(editOffer.approved_at, "short", tz) : ""}.
                </p>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(!editOffer.approval_status || editOffer.approval_status === "rejected") && (
                  <button
                    onClick={() => submitApprovalMutation.mutate(editOffer.id)}
                    disabled={!editOffer.approver_id || submitApprovalMutation.isPending}
                    style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: NAVY, borderColor: NAVY, opacity: editOffer.approver_id ? 1 : 0.5 }}
                  >
                    {submitApprovalMutation.isPending ? "Submitting…" : "Submit for Approval"}
                  </button>
                )}
                {editOffer.approval_status === "pending" && editOffer.approver_id === user?.id && (
                  <div style={{ width: "100%" }}>
                    {!user?.signature_url && (
                      <p style={{ fontSize: 11, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "6px 10px", margin: "0 0 8px" }}>
                        No signature image on file — your typed name will be used as the stamp on the PDF. Upload one anytime via "My Signature" in the profile menu.
                      </p>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
                      <input
                        value={approveTypedName}
                        onChange={e => setApproveTypedName(e.target.value)}
                        placeholder="Type your full name to sign"
                        style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                      />
                    </div>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 11, color: "#475569", marginBottom: 8, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={approveConsentChecked}
                        onChange={e => setApproveConsentChecked(e.target.checked)}
                        style={{ marginTop: 2 }}
                      />
                      I agree that clicking below constitutes my electronic signature on this document.
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        onClick={() => approveMutation.mutate({ id: editOffer.id, typedName: approveTypedName.trim(), consentChecked: approveConsentChecked })}
                        disabled={approveMutation.isPending || !approveTypedName.trim() || !approveConsentChecked}
                        style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#15803d", borderColor: "#86efac", fontWeight: 600, opacity: (approveTypedName.trim() && approveConsentChecked) ? 1 : 0.5 }}
                      >
                        {approveMutation.isPending ? "Approving…" : "✓ Approve & Sign"}
                      </button>
                      <button
                        onClick={() => setShowReject(v => !v)}
                        style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#dc2626", borderColor: "#fecaca" }}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                )}
                {editOffer.approval_status === "pending" && editOffer.approver_id !== user?.id && (
                  <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>Waiting on {editOffer.approver_name} to sign off.</p>
                )}
              </div>

              {showReject && (
                <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                  <input
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Reason for rejecting…"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    onClick={() => rejectMutation.mutate({ id: editOffer.id, reason: rejectReason })}
                    disabled={!rejectReason.trim() || rejectMutation.isPending}
                    style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#dc2626", borderColor: "#fecaca", fontWeight: 600 }}
                  >
                    {rejectMutation.isPending ? "…" : "Confirm Reject"}
                  </button>
                </div>
              )}
            </div>

            {/* Signing timeline — evidentiary trail (AC5) */}
            <div style={{ marginBottom: 14 }}>
              <button
                type="button"
                onClick={() => setShowSignatureEvents(v => !v)}
                style={{ background: "none", border: "none", color: "#64748b", fontSize: 11, cursor: "pointer", padding: 0, textDecoration: "underline" }}
              >
                {showSignatureEvents ? "Hide" : "Show"} signing timeline (IP, device, consent)
              </button>
              {showSignatureEvents && (
                <div style={{ marginTop: 8, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  {(signatureEvents as any[]).length === 0 ? (
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: 0, padding: "10px 12px" }}>No events recorded yet.</p>
                  ) : (
                    (signatureEvents as any[]).map((ev: any, i: number) => (
                      <div key={i} style={{ padding: "8px 12px", borderTop: i > 0 ? "1px solid #f1f5f9" : "none", fontSize: 11, color: "#475569" }}>
                        <span style={{ fontWeight: 700, textTransform: "capitalize" }}>{ev.event_type}</span>
                        {" "}by {ev.actor_kind} · {ev.occurred_at ? formatDate(ev.occurred_at, "short", tz) : ""}
                        <br />
                        <span style={{ color: "#94a3b8" }}>
                          IP {ev.ip_address || "unknown"}
                          {ev.consent_text_version ? ` · consent ${ev.consent_text_version}` : ""}
                          {ev.document_sha256 ? ` · doc ${ev.document_sha256.slice(0, 12)}…` : ""}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Accept link */}
            {editOffer.accept_link && (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#15803d", marginBottom: 4 }}>Candidate Acceptance Link</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <code style={{ fontSize: 11, color: "#166534", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{editOffer.accept_link}</code>
                  <button onClick={() => { navigator.clipboard.writeText(editOffer.accept_link); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    style={{ ...btnOutline, padding: "4px 10px", fontSize: 11, flexShrink: 0 }}>
                    {copied ? "✓ Copied" : "Copy"}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Offer Letter Content</label>
              <textarea
                value={editOffer.content || ""}
                onChange={e => setEditOffer((o: any) => ({ ...o, content: e.target.value }))}
                rows={18}
                style={{ ...inputStyle, fontFamily: "var(--font-sans)", lineHeight: 1.7, resize: "vertical" }}
              />
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => setEditOffer(null)} style={btnOutline}>Close</button>
              <button onClick={() => handleDownload(editOffer.id)} disabled={downloading === editOffer.id} style={{ ...btnOutline, color: NAVY, borderColor: NAVY }}>
                {downloading === editOffer.id ? "Generating PDF…" : "⬇ Download PDF"}
              </button>
              {(editOffer.approval_status === "approved" || editOffer.status === "accepted") && (
                <button
                  onClick={() => handleDownloadCertificate(editOffer.id)}
                  disabled={downloadingCertificate === editOffer.id}
                  style={{ ...btnOutline, color: "#0f766e", borderColor: "#5eead4" }}
                  title="Certificate of Completion — signing evidence (IP, timestamp, consent, document hash)"
                >
                  {downloadingCertificate === editOffer.id ? "Generating…" : "📜 Certificate"}
                </button>
              )}
              <button
                onClick={async () => {
                  // Flipping the Status dropdown to "sent" and saving emails the
                  // candidate — give it the same unsigned-send warning as the
                  // Send buttons instead of leaving a silent bypass (AC13).
                  const original = offers.find((o: any) => o.id === editOffer.id);
                  if (
                    editOffer.status === "sent" && original?.status !== "sent" &&
                    editOffer.approval_status !== "approved"
                  ) {
                    const ok = await confirm(
                      "Saving with status \"sent\" emails the offer to the candidate, and it hasn't been signed off by an approver yet. Send anyway?",
                      { title: "Send without sign-off?", confirmLabel: "Save & Send", danger: true },
                    );
                    if (!ok) return;
                  }
                  updateMutation.mutate({ id: editOffer.id, data: editOffer });
                }}
                disabled={updateMutation.isPending}
                style={{ ...btnOutline, color: "#0f172a", fontWeight: 600 }}
              >
                {updateMutation.isPending ? "Saving…" : "💾 Save"}
              </button>
              <button
                onClick={async () => {
                  await updateMutation.mutateAsync({ id: editOffer.id, data: editOffer });
                  handleSend(editOffer.id);
                }}
                disabled={sending === editOffer.id || updateMutation.isPending}
                style={{ ...btnPrimary, background: "linear-gradient(135deg,#15803d,#22c55e)" }}
              >
                {sending === editOffer.id ? "Sending…" : "📧 Send to Candidate"}
              </button>
            </div>
            {editOffer.approval_status === "approved" && editOffer.status === "accepted" ? (
              <p style={{ margin: "10px 0 0", fontSize: 11, color: "#0f766e", textAlign: "right" }}>
                ✓ Fully executed — the signed offer and Certificate of Completion were emailed to the candidate, approver, and you.
              </p>
            ) : (editOffer.approval_status === "approved" || editOffer.status === "accepted") ? (
              <p style={{ margin: "10px 0 0", fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
                Once both the approver and the candidate have signed, the signed offer and Certificate of Completion are emailed to everyone automatically.
              </p>
            ) : null}
          </div>
        </div>
      )}

      {/* Offers list */}
      {isLoading ? (
        <div style={{ display: "grid", gap: 10 }}><SkeletonRow cols={3} /><SkeletonRow cols={3} /><SkeletonRow cols={3} /></div>
      ) : offers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto", display: "block" }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
          </svg>
          <p style={{ fontSize: 15, margin: "16px 0 6px", fontWeight: 600, color: "#64748b" }}>No offer letters yet</p>
          <p style={{ fontSize: 13, margin: "0 0 20px", color: "#94a3b8" }}>Generate AI-drafted, editable offer letters with PDF download.</p>
          <button onClick={() => { setGenForm(blankGenForm()); setShowGenerate(true); }} style={{ ...btnPrimary, display: "inline-flex", alignItems: "center", gap: 6 }}>
            + Generate Offer
          </button>
        </div>
      ) : (
        visibleOffers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b", background: "white", border: "1px solid #e2e8f0", borderRadius: 12 }}>
          No offers match “{search}”.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visibleOffers.map((o: any) => {
            const sc = STATUS_COLORS[o.status] || STATUS_COLORS.draft;
            return (
              <div key={o.id} style={{
                background: "white", borderRadius: 12, border: "1px solid #e2e8f0",
                padding: isMobile ? "14px 16px" : "16px 20px", display: "flex", gap: 12,
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, color: NAVY, fontSize: 14, overflowWrap: "anywhere" }}>{o.candidate_name}</span>
                    <span style={{ fontSize: 13, color: "#64748b", overflowWrap: "anywhere" }}>→ {o.job_title}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                      background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                    }}>{o.status}</span>
                    {o.approval_status && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                        background: APPROVAL_COLORS[o.approval_status]?.bg,
                        color: APPROVAL_COLORS[o.approval_status]?.text,
                        border: `1px solid ${APPROVAL_COLORS[o.approval_status]?.border}`,
                      }}>{APPROVAL_COLORS[o.approval_status]?.label}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    {o.currency_symbol}{(o.salary || 0).toLocaleString()}/mo
                    {o.start_date && ` · Start: ${formatDate(o.start_date)}`}
                    {o.expiry_date && ` · Expires: ${formatDate(o.expiry_date)}`}
                    {o.created_at && ` · Created ${formatDate(o.created_at, "short", tz)}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                  <button onClick={() => setEditOffer(o)} style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12 }}>✏ Edit</button>
                  <button onClick={() => handleDownload(o.id)} disabled={downloading === o.id}
                    style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: NAVY, borderColor: NAVY }}>
                    {downloading === o.id ? "…" : "⬇ PDF"}
                  </button>
                  {(o.approval_status === "approved" || o.status === "accepted") && (
                    <button
                      onClick={() => handleDownloadCertificate(o.id)}
                      disabled={downloadingCertificate === o.id}
                      style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#0f766e", borderColor: "#5eead4" }}
                      title="Certificate of Completion — signing evidence (IP, timestamp, consent, document hash)"
                    >
                      {downloadingCertificate === o.id ? "…" : "📜 Certificate"}
                    </button>
                  )}
                  {o.status === "draft" && (
                    <button onClick={() => handleSend(o.id)} disabled={sending === o.id}
                      style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#15803d", borderColor: "#86efac", fontWeight: 600 }}>
                      {sending === o.id ? "Sending…" : "📧 Send"}
                    </button>
                  )}
                  <AsyncButton onClick={async () => { if (await confirm("Delete this offer letter?", { confirmLabel: "Delete" })) deleteMutation.mutate(o.id); }}
                    style={{ ...btnOutline, padding: isMobile ? "11px 14px" : "7px 12px", minHeight: isMobile ? 44 : undefined, fontSize: 12, color: "#dc2626", borderColor: "#fecaca" }}>
                    🗑
                  </AsyncButton>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
